import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddonPackage, AddonPackageApartment, AddonPackageOrder } from '../../../../models/addon-package';
import { APIService } from '../../../../services/http/http.service';
import { DeleteResponse, EntityResponse } from '../../../../utils/types';

@Injectable()
export class AddonPackageService extends APIService<AddonPackage> {
  public route = 'addon-package';

  // Admin: Get all addon packages
  getAll(): Observable<AddonPackage[]> {
    return this.http
      .get<{ Collection: AddonPackage[] }>(`${this.apiUrl}/${this.route}`, this.options())
      .pipe(map((res) => res.Collection || []));
  }

  // Admin: Get single addon package
  getOne(id: string): Observable<EntityResponse<AddonPackage>> {
    if (!id) {
      throw new Error('id is required');
    }

    return this.http.get<EntityResponse<AddonPackage>>(`${this.apiUrl}/${this.route}/${id}`, this.options());
  }

  // Admin: Create addon package
  create(data: Partial<AddonPackage>): Observable<EntityResponse<AddonPackage>> {
    return this.http.post<EntityResponse<AddonPackage>>(`${this.apiUrl}/${this.route}`, data, this.options());
  }

  // Admin: Update addon package
  update(id: string, data: Partial<AddonPackage>): Observable<EntityResponse<AddonPackage>> {
    if (!id) {
      throw new Error('id is required');
    }

    return this.http.post<EntityResponse<AddonPackage>>(`${this.apiUrl}/${this.route}/${id}`, data, this.options());
  }

  // Admin: Delete addon package
  override delete(id: string): Observable<DeleteResponse> {
    if (!id) {
      throw new Error('id is required');
    }

    return this.http.delete<DeleteResponse>(`${this.apiUrl}/${this.route}/${id}`, this.options());
  }

  // Admin: Get apartment connections for a package in a project
  getApartmentConnections(projectId: number, packageId: string): Observable<AddonPackageApartment[]> {
    if (!projectId || !packageId) {
      throw new Error('projectId and packageId are required');
    }

    return this.http
      .get<{
        Collection: AddonPackageApartment[];
      }>(`${this.apiUrl}/project/${projectId}/${this.route}/${packageId}/apartment`, this.options())
      .pipe(map((res) => res.Collection || []));
  }

  // Admin: Create apartment connection
  createApartmentConnection(
    projectId: number,
    packageId: string,
    apartmentId: number,
    data: Partial<AddonPackageApartment>,
  ): Observable<EntityResponse<AddonPackageApartment>> {
    if (!projectId || !packageId || !apartmentId) {
      throw new Error('projectId, packageId, and apartmentId are required');
    }

    return this.http.post<EntityResponse<AddonPackageApartment>>(
      `${this.apiUrl}/project/${projectId}/${this.route}/${packageId}/apartment/${apartmentId}`,
      data,
      this.options(),
    );
  }

  // Admin: Update apartment connection
  updateApartmentConnection(
    projectId: number,
    packageId: string,
    apartmentId: number,
    data: Partial<AddonPackageApartment>,
  ): Observable<EntityResponse<AddonPackageApartment>> {
    if (!projectId || !packageId || !apartmentId) {
      throw new Error('projectId, packageId, and apartmentId are required');
    }

    return this.http.post<EntityResponse<AddonPackageApartment>>(
      `${this.apiUrl}/project/${projectId}/${this.route}/${packageId}/apartment/${apartmentId}`,
      data,
      this.options(),
    );
  }

  // Admin: Delete apartment connection
  deleteApartmentConnection(projectId: number, packageId: string, apartmentId: number): Observable<void> {
    if (!projectId || !packageId || !apartmentId) {
      throw new Error('projectId, packageId, and apartmentId are required');
    }

    return this.http.delete<void>(
      `${this.apiUrl}/project/${projectId}/${this.route}/${packageId}/apartment/${apartmentId}`,
      this.options(),
    );
  }

  // Admin: Get all package orders
  getAllOrders(): Observable<AddonPackageOrder[]> {
    return this.http
      .get<{ Collection: AddonPackageOrder[] }>(`${this.apiUrl}/${this.route}-order`, this.options())
      .pipe(map((res) => res.Collection || []));
  }

  // Admin: Accept an order
  acceptOrder(orderId: string): Observable<EntityResponse<AddonPackageOrder>> {
    if (!orderId) {
      throw new Error('orderId is required');
    }

    return this.http.get<EntityResponse<AddonPackageOrder>>(
      `${this.apiUrl}/${this.route}-order/${orderId}/accept`,
      this.options(),
    );
  }

  // Admin: Delete an order
  deleteOrder(orderId: string): Observable<void> {
    if (!orderId) {
      throw new Error('orderId is required');
    }

    return this.http.delete<void>(`${this.apiUrl}/${this.route}-order/${orderId}`, this.options());
  }

  // Client: Get available packages for apartment
  getAvailableForApartment(apartmentId: number): Observable<AddonPackage[]> {
    if (!apartmentId) {
      throw new Error('apartmentId is required');
    }

    return this.http
      .get<{
        Collection: AddonPackage[];
      }>(`${this.apiUrl}/client/apartment/${apartmentId}/${this.route}`, this.options())
      .pipe(map((res) => res.Collection || []));
  }

  // Client: Get single package details for apartment
  getOneForApartment(apartmentId: number, packageId: string): Observable<EntityResponse<AddonPackage>> {
    if (!apartmentId || !packageId) {
      throw new Error('apartmentId and packageId are required');
    }

    return this.http.get<EntityResponse<AddonPackage>>(
      `${this.apiUrl}/client/apartment/${apartmentId}/${this.route}/${packageId}`,
      this.options(),
    );
  }

  // Client: Create order and get Signicat URL
  createOrder(apartmentId: number, packageId: string): Observable<{ redirectUrl: string }> {
    if (!apartmentId || !packageId) {
      throw new Error('apartmentId and packageId are required');
    }

    return this.http.get<{ redirectUrl: string }>(
      `${this.apiUrl}/client/apartment/${apartmentId}/${this.route}/${packageId}/order`,
      this.options(),
    );
  }
}
